import React from "react";
import { ChevronDown } from "lucide-react";
import GeotabBadge from "./GeotabBadge.jsx";
import { HoverRows } from "./GeotabHover.jsx";
import { Sparkline } from "../DashboardKpiStrip.jsx";
import { buildSparkline } from "../../utils/dashboardComparison.js";
import { planBarCells, planBarStops } from "./geotabPlanBar.js";
import { dayValue, METRICS, valueOf, weekDelta, weekdayScopes, weekScopes } from "./geotabDashboardSeries.js";
import { dayLabel, Legend, longDay, STAGE_LEGEND, StageDailyChart, WeekChart } from "./GeotabDashboardCharts.jsx";
import { hoursTone, plural, REGULAR_DAYS, watchItems } from "./geotabDashboard.js";
import { fmtDelta, fmtDuration, fmtTime } from "./geotabFormat.js";
import { writeGeotabSession } from "./geotabPreferences.js";
import "../DashboardCharts.css";
import "./GeotabDashboard.css";

// Vue d'ensemble du Tableau de bord Geotab, sur le modèle du Tableau de bord principal : bandeau des mesures, puis ce qui fait un bon
// dispatch (route et temps sur place, prévu et réel par journée) à côté de « À surveiller » ; en dessous, la mesure choisie par
// semaine, par chauffeur et son détail ; à droite, la conduite, les jours de semaine, puis les Chargés PM et les P1, repliés.
const ONE = new Intl.NumberFormat("fr-CA", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const CELL = "border-t border-slate-100 px-1.5 py-[3px] text-[12px] whitespace-nowrap tabular-nums";
const HEAD = "px-1.5 pb-1 pt-0 text-left text-[10px] font-bold uppercase tracking-wide text-slate-600 whitespace-nowrap";
const STAGES = [["route", "Route par trajet"], ["loading", "Sur place · chargement"], ["delivery", "Sur place · livraison"]];
const clamp = (ratio) => Math.max(0, Math.min(1, ratio || 0));

export const scopeLabel = (scope) => {
  const days = scope?.days || [];
  if (!days.length) return "";
  const range = `du ${dayLabel(days[0].date)} au ${dayLabel(days.at(-1).date)}`;
  return scope.key === "week" ? `Semaine ${range}` : `Période ${range} · ${plural(days.length, "journée")}`;
};

function Card({ title, subtitle, aside, footer, className = "", children }) {
  return (
    <section className={`dashboard-card rounded-[4px] ${className}`} aria-label={title}>
      <header className="dashboard-card-header">
        <div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
        {aside}
      </header>
      <div className="dashboard-card-body">{children}</div>
      {footer}
    </section>
  );
}

// Ligne de liste : libellé et jauge, valeur, précision (comme « Camion moyen »).
function Row({ label, value, sub, gauge, warning = false, chip = null, strong = false }) {
  return (
    <div className="dashboard-metric-row" data-strong={strong ? "true" : undefined}>
      <span className="dashboard-metric-label">{label}
        {gauge != null && <span className="dashboard-metric-gauge" aria-hidden="true"><span data-tone={warning ? "warning" : undefined} style={{ width: `${clamp(gauge) * 100}%` }} /></span>}
      </span>
      <span className="dashboard-metric-value">{chip ? <span className="inline-block min-w-[62px]"><GeotabBadge tone={chip}>{value}</GeotabBadge></span> : value}</span>
      <span className="dashboard-metric-delta">{sub}</span>
    </div>
  );
}

const DayLink = ({ date, onOpen, long = false }) => <button type="button" className="dashboard-watch-link !mt-0" onClick={() => onOpen(date)}>{long ? longDay(date) : dayLabel(date)}</button>;

// Ce qui fait un bon dispatch : la route entre deux arrêts et le temps sur place au chargement et à la livraison, prévu et réel
// par journée. À gauche, l'écart moyen de la portée choisie ; la mesure choisie dans le bandeau est mise en avant.
function StagesCard({ scopes, scope, metric, onOpenDay }) {
  const current = scopes[scope] || scopes.period;
  const selected = new Set(current.days.map((d) => d.date));
  return (
    <Card title="Le plan du Dispatch est-il juste ?" className="geotab-dash-chart geotab-dash-stages" aside={<Legend items={STAGE_LEGEND} />}
      subtitle={`${scopeLabel(current)} · route et temps sur place, prévu et réel par journée · cliquez une journée pour l'ouvrir dans Dispatch vs Geotab`}>
      {STAGES.map(([key, label], i) => {
        const s = current.durations?.[key];
        return (
          <div key={key} className="geotab-stage-row" data-active={metric.stage === key ? "true" : undefined} data-geotab-stage={key}>
            <div className="geotab-stage-head">
              <span className="dashboard-kpi-label">{label}</span>
              <span className="geotab-stage-gap">{s?.count ? fmtDelta(Math.round(s.real) - Math.round(s.planned)) : "—"}</span>
              <span className="dashboard-kpi-compare">{s?.count ? `${fmtDuration(s.planned)} prévues → ${fmtDuration(s.real)}` : "aucune mesure"}</span>
              {s?.count > 0 && <span className="dashboard-kpi-compare">{`${s.near_pct} % dans la tolérance · écart typique ${fmtDuration(s.spread)}`}</span>}
            </div>
            <div className="geotab-dash-plot">
              <StageDailyChart days={scopes.period.days} stage={key} label={label} selected={selected} onOpenDay={onOpenDay} showDates={i === STAGES.length - 1} />
            </div>
          </div>
        );
      })}
    </Card>
  );
}

export function KpiStrip({ scopes, scope, metricKey, onMetric, tolerance }) {
  const current = scopes[scope] || scopes.period;
  const cells = Object.fromEntries(planBarCells(current.bar, "period", { timing: { durations: current.durations }, tolerance }).map((c) => [c.key, c]));
  const stops = planBarStops(current.bar);
  const days = scopes.period.days.map((d) => ({ ...d, dispatchDate: d.date }));
  const selected = new Set(current.days.map((d) => d.date));
  return (
    <section className="dashboard-kpi-strip" aria-label="Mesures principales">
      <div className="dashboard-kpi-grid geotab-kpi-grid rounded-[4px]">
        {METRICS.map((m) => {
          const cell = cells[m.key];
          const value = valueOf(m, current);
          const delta = scope === "week" ? weekDelta(m, current, scopes.previous) : null;
          const rows = [...cell.hover, ...(m.key === "stops" ? stops.map((i) => [i.label, `${i.value} · ${i.count || i.missing}`]) : [])];
          return (
            <HoverRows key={m.key} title={m.label} rows={rows}>
              <button type="button" className="dashboard-kpi-cell geotab-kpi-cell" aria-pressed={metricKey === m.key} onClick={() => onMetric(m.key)} data-geotab-kpi={m.key}>
                <span className="dashboard-kpi-label">{m.label}</span>
                <span className="dashboard-kpi-main">
                  <span className="dashboard-kpi-value">{value == null ? "—" : m.format(value)}</span>
                  <Sparkline bars={buildSparkline(days, (d) => dayValue(m, d), selected, 30)} />
                </span>
                <span className="dashboard-kpi-delta">
                  {delta ? <><span data-tone={delta.tone}>{delta.label}</span> <span className="dashboard-kpi-compare">vs semaine précédente</span></>
                    : <span className="dashboard-kpi-compare">{cell.sub.map((part, j) => <React.Fragment key={part.text}>{j ? " · " : ""}<span className="whitespace-nowrap">{part.text}</span></React.Fragment>)}</span>}
                </span>
              </button>
            </HoverRows>
          );
        })}
      </div>
    </section>
  );
}

function WatchCard({ data, onOpenDay, onOpenView, onDrivers }) {
  const items = watchItems(data);
  const warnings = items.filter((i) => i.tone === "warning").length;
  const action = (item) => {
    if (item.drivers) return <button type="button" className="dashboard-watch-link" onClick={onDrivers}>Par chauffeur →</button>;
    if (item.view) return <button type="button" className="dashboard-watch-link" onClick={() => onOpenView(item.view)}>Camions et boîtiers →</button>;
    if (item.key === "duty") return <button type="button" className="dashboard-watch-link" onClick={() => { writeGeotabSession({ calibration_tab: "drivers" }); onOpenView("calibration"); }}>Calibrage › Chauffeurs →</button>;
    return null;
  };
  return (
    <section className="dashboard-card dashboard-watch rounded-[4px]" aria-label="À surveiller">
      <header className="dashboard-card-header">
        <div><h2>À surveiller</h2><p className="dashboard-watch-count" data-tone={warnings ? "warning" : "ok"}>{warnings ? `${plural(warnings, "point")} à vérifier` : "Rien à vérifier"}</p></div>
        <p className="dashboard-watch-scope">{`Du ${dayLabel(data.from)} au ${dayLabel(data.to)}`}</p>
      </header>
      <ul className="dashboard-watch-list">
        {items.map((item) => (
          <li key={item.key} className="dashboard-watch-item">
            <span className="dashboard-watch-mark rounded-none" data-tone={item.tone} aria-hidden="true" />
            <div className="min-w-0">
              <p className="dashboard-watch-title">{item.title}</p>
              {item.detail && <p className="dashboard-watch-detail">{item.detail}</p>}
              {item.days?.length > 0 && (
                <p className="dashboard-watch-detail">Journées à revoir : {item.days.map((d, j) => (
                  <React.Fragment key={d.date}>{j ? " · " : ""}<button type="button" className="dashboard-watch-link !mt-0" onClick={() => onOpenDay(d.date)}>{d.label}</button></React.Fragment>
                ))}</p>
              )}
              {action(item)}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DriversCard({ data, scopes, scope, metric, onDrivers, onOpenDay }) {
  if (metric.stage) {
    // Pas de durée par chauffeur dans le Tableau de bord : les journées où l'étape s'écarte le plus du prévu, à ouvrir.
    const current = scopes[scope] || scopes.period;
    const far = current.days.filter((d) => d.durations?.[metric.stage]?.count).map((d) => ({ date: d.date, s: d.durations[metric.stage] }))
      .map((f) => ({ ...f, gap: Math.round(f.s.real) - Math.round(f.s.planned) })).sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap)).slice(0, 6);
    const max = Math.max(...far.map((f) => Math.abs(f.gap)), 1);
    return (
      <Card title="Journées les plus éloignées du prévu" subtitle={`${scopeLabel(current)} · ${metric.label.toLowerCase()}`}>
        <div className="dashboard-metric-rows">
          {far.map((f) => <Row key={f.date} label={<DayLink date={f.date} onOpen={onOpenDay} long />} value={fmtDelta(f.gap)}
            sub={`${fmtDuration(f.s.planned)} → ${fmtDuration(f.s.real)}`} gauge={Math.abs(f.gap) / max} />)}
        </div>
      </Card>
    );
  }
  const all = (data.drivers || []).map((d) => ({ name: d.name, days: d.days, value: metric.value(d.bar, null) })).filter((r) => r.value != null);
  // Pire en premier ; les chauffeurs de moins de 10 jours ne sont pas classés, trop peu de journées.
  const rows = all.filter((r) => r.days >= REGULAR_DAYS).sort((a, b) => metric.better * (a.value - b.value));
  const max = Math.max(...rows.map((r) => Math.abs(r.value)), 1);
  const others = all.length - rows.length;
  return (
    <Card title="Par chauffeur" subtitle={`Période · ${metric.label.toLowerCase()} · ${metric.better > 0 ? "du plus bas au plus haut" : "du plus haut au plus bas"}`}
      footer={<p className="dashboard-card-note">{others ? `${plural(others, "chauffeur")} de moins de ${REGULAR_DAYS} jours non classé${others > 1 ? "s" : ""}. ` : ""}
        <button type="button" className="dashboard-watch-link !mt-0" onClick={onDrivers}>Tableau complet par chauffeur →</button></p>}>
      <div className="dashboard-metric-rows geotab-dash-scroll">
        {rows.map((r) => (
          <Row key={r.name} label={r.name} value={metric.format(r.value)} sub={`${r.days} j.`}
            chip={metric.key === "hours" ? hoursTone(r.value) : null}
            gauge={metric.key === "hours" ? null : metric.unit === "pts" ? r.value / 100 : Math.abs(r.value) / max}
            warning={metric.threshold != null && (metric.unit === "pts" ? r.value < metric.threshold : r.value > metric.threshold)} />
        ))}
      </div>
    </Card>
  );
}

function ContextCard({ data, scopes, scope, metric, onOpenDay }) {
  if (metric.key === "stops") {
    const days = data.worst_days || [];
    const max = Math.max(...days.map((d) => d.missing), 1);
    return (
      <Card title="Journées les plus manquées" subtitle="Période · arrêts non faits, camions hors ordre et retours en retard">
        <div className="dashboard-metric-rows">
          {days.length ? days.map((d) => <Row key={d.date} label={<DayLink date={d.date} onOpen={onOpenDay} long />} value={plural(d.missing, "non fait")}
            sub={`${d.trucks} camions`} gauge={d.missing / max} warning />) : <p className="dashboard-card-note">Aucune journée à revoir.</p>}
        </div>
      </Card>
    );
  }
  if (metric.key === "hours") {
    const bands = (data.hour_bands || []).filter((b) => b.days);
    const max = Math.max(...bands.map((b) => b.days), 1);
    const over = data.fleet.over_max;
    const shortLong = bands.length > 1 && bands[0].gap > 30 && bands.at(-1).gap < -30;
    return (
      <Card title="Selon la durée prévue" subtitle="Période · journées complètes · écart moyen réel − prévu">
        <div className="dashboard-metric-rows">
          {bands.map((b) => <Row key={b.label} label={b.label} value={fmtDelta(b.gap)} chip={hoursTone(b.gap)} sub={`${b.days} j.`} gauge={b.days / max} />)}
        </div>
        {shortLong && <p className="dashboard-card-note">Plus la journée prévue est courte, plus elle déborde ; au-delà de 10 h, elle finit plus tôt.</p>}
        {over.days > 0 && <p className="dashboard-card-note">{`Au-delà du maximum du camion : ${over.real} réelle${over.real > 1 ? "s" : ""} · ${over.planned} déjà prévue${over.planned > 1 ? "s" : ""} au-delà (sur ${over.days}).`}</p>}
      </Card>
    );
  }
  return null;
}

function DrivingCard({ data }) {
  const f = data.fleet;
  const kmGap = f.km.planned ? 100 * (f.km.real / f.km.planned - 1) : null;
  const identified = f.identified.days ? Math.round((100 * (f.identified.days - f.identified.other)) / f.identified.days) : null;
  const events = Object.values(f.safety || {}).reduce((n, v) => n + v, 0);
  return (
    <Card title="Conduite et conformité" subtitle="Période · mesures habituelles des flottes Geotab">
      <div className="dashboard-metric-rows">
        <Row label="Excès de vitesse / 100 km" value={f.km_driven ? ONE.format((100 * f.speeding) / f.km_driven) : "—"} sub="" />
        <Row label="Ralenti / camion-jour" value={f.truck_days ? fmtDuration(f.idle_minutes / f.truck_days) : "—"} sub="" />
        <Row label="Km réels vs prévus" value={kmGap == null ? "—" : `${kmGap >= 0 ? "+" : "−"}${ONE.format(Math.abs(kmGap))} %`} sub={`${f.km.days} j.`} />
        <Row label="Chauffeur identifié dans Geotab" value={identified == null ? "—" : `${identified} %`} gauge={identified == null ? null : identified / 100}
          warning={identified != null && identified < 95} sub={f.identified.other ? `${f.identified.other} autre nom` : ""} />
        <Row label="Service de plus de 14 h" value={f.duty.long} sub={f.duty.median != null ? `méd. ${fmtDuration(f.duty.median)}` : ""} />
        <Row label="Événements de sécurité" value={events} sub={events ? "" : "à vérifier"} />
      </div>
    </Card>
  );
}

function WeekdayCard({ scopes, metric }) {
  const groups = weekdayScopes(scopes.period.days);
  const values = groups.map((g) => valueOf(metric, g));
  const max = Math.max(...values.map((v) => Math.abs(v ?? 0)), 1);
  return (
    <section className="dashboard-card dashboard-metric-list dashboard-weekday-list rounded-[4px]" aria-label="Par jour de semaine">
      <header className="dashboard-card-header"><div><h2>Par jour de semaine</h2><p>{`${metric.label} · période`}</p></div></header>
      <div className="dashboard-weekday-grid">
        {groups.map((g, i) => (
          <div key={g.name} className="dashboard-weekday" data-geotab-weekday={g.name}>
            <span className="dashboard-metric-label">{g.name}</span>
            <span className="dashboard-metric-value">{values[i] == null ? "—" : metric.format(values[i])}</span>
            <span className="dashboard-metric-gauge" aria-hidden="true"><span style={{ width: `${clamp(metric.unit === "pts" ? values[i] / 100 : Math.abs(values[i] ?? 0) / max) * 100}%` }} /></span>
          </div>
        ))}
      </div>
    </section>
  );
}

// Carte repliée : quelques lignes, la liste complète sous « Détail ».
function FoldCard({ title, subtitle, rows, children }) {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  return (
    <Card title={title} subtitle={subtitle} aside={(
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}
        className="inline-flex h-6 flex-none items-center gap-1 rounded-[4px] border border-slate-300 bg-white px-2 text-[11px] font-bold text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-accent">
        Détail <ChevronDown size={14} aria-hidden="true" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
    )}>
      <div className="dashboard-metric-rows">{rows}</div>
      <div id={id} hidden={!open} className="mt-2 border-t border-slate-200 pt-2">{children}</div>
    </Card>
  );
}

function PreloadFold({ data, onOpenDay }) {
  const { pm, preloads } = data.bar;
  return (
    <FoldCard title="Chargés PM et préchargements" subtitle="Période · le soir pour le lendemain" rows={<>
      <Row label="Chargés PM faits" value={pm.planned ? `${pm.done} / ${pm.planned}` : "—"} gauge={pm.planned ? pm.done / pm.planned : null}
        warning={pm.done < pm.planned} sub={pm.planned > pm.done ? plural(pm.planned - pm.done, "non fait") : ""} />
      <Row label="Préchargements partis chargés" value={preloads.days ? `${preloads.days - preloads.reloaded} / ${preloads.days}` : "—"}
        gauge={preloads.days ? (preloads.days - preloads.reloaded) / preloads.days : null} sub={preloads.days ? plural(preloads.orders, "commande") : ""} />
      <Row label="Rechargés le matin" value={preloads.reloaded} sub="" />
    </>}>
      {data.pm?.length > 0 && (
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full min-w-0 border-collapse">
            <thead><tr>{["Journée", "Camion", "Lieu", ""].map((h) => <th key={h} className={HEAD}>{h}</th>)}</tr></thead>
            <tbody>
              {data.pm.map((x) => (
                <tr key={`${x.date}-${x.truck}-${x.site}-${x.planned}`}>
                  <td className={CELL}><DayLink date={x.date} onOpen={onOpenDay} /></td>
                  <td className={CELL}>{x.truck}</td>
                  <td className={`${CELL} max-w-[130px] truncate`}>{x.site}</td>
                  <td className={CELL}><span className="inline-block min-w-[62px]"><GeotabBadge tone={x.real == null ? "bad" : "ok"}>{x.real == null ? "Non fait" : "Fait"}</GeotabBadge></span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.pm_count > (data.pm?.length || 0) && <p className="dashboard-card-note">{`Les ${data.pm.length} plus récents sur ${data.pm_count}.`}</p>}
      {(data.reloaded || []).map((x) => (
        <p key={`${x.date}-${x.truck}-${x.arrival}`} className="m-0 mt-1 flex min-w-0 items-center gap-2 text-[12px] text-slate-800">
          <span className="inline-block w-[112px] shrink-0"><GeotabBadge tone="bad">Rechargé le matin</GeotabBadge></span>
          <span className="min-w-0 truncate"><DayLink date={x.date} onOpen={onOpenDay} />{` · ${x.truck} · ${x.site || "lieu de chargement"} · ${fmtDuration(x.minutes)} sur place`}</span>
        </p>
      ))}
    </FoldCard>
  );
}

function P1Fold({ data, onOpenDay }) {
  const { p1, p2, windows } = data.bar;
  const rows = data.p1 || [];
  const [before, after] = windows?.P1 || [30, 15];
  const plannedOut = rows.filter((x) => x.planned_out).length;
  const status = (x) => (x.status === "ok" ? ["ok", "À l'heure"] : x.status === "missing" ? ["bad", "Non faite"] : [x.status === "early" ? "early" : "bad", fmtDelta(x.real - x.requested)]);
  return (
    <FoldCard title="P1 : heures demandées" subtitle={`Période · de ${before} min avant à ${after} min après`} rows={<>
      <Row label="P1 dans la fenêtre" value={p1.count ? `${p1.ok} / ${p1.count}` : "—"} gauge={p1.count ? p1.ok / p1.count : null} warning={p1.ok < p1.count}
        sub={[p1.early && `${p1.early} en avance`, p1.late && `${p1.late} en retard`, p1.missing && plural(p1.missing, "non faite")].filter(Boolean).join(" · ")} />
      <Row label={`P2 dans la fenêtre (± ${windows?.P2?.[1] ?? 60} min)`} value={p2.count ? `${p2.ok} / ${p2.count}` : "—"} gauge={p2.count ? p2.ok / p2.count : null}
        sub={p2.count ? `${p2.late} en retard` : ""} />
    </>}>
      {rows.length ? (
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full min-w-0 border-collapse">
            <thead><tr>{["Journée", "Camion", "Lieu", "Demandée", "Réelle", ""].map((h) => <th key={h} className={HEAD}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((x) => {
                const [tone, text] = status(x);
                return (
                  <tr key={`${x.date}-${x.truck}-${x.site}-${x.requested}`}>
                    <td className={CELL}><DayLink date={x.date} onOpen={onOpenDay} /></td>
                    <td className={CELL}>{x.truck}</td>
                    <td className={`${CELL} max-w-[110px] truncate`}>{x.site}</td>
                    <td className={`${CELL} ${x.planned_out ? "font-bold text-[#c5524a]" : ""}`}>{fmtTime(x.requested)}</td>
                    <td className={`${CELL} font-semibold`}>{fmtTime(x.real)}</td>
                    <td className={CELL}><span className="inline-block min-w-[62px]"><GeotabBadge tone={tone}>{text}</GeotabBadge></span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <p className="dashboard-card-note">Aucune P1 sur la période.</p>}
      {data.p1_count > rows.length && <p className="dashboard-card-note">{`Les ${rows.length} premières sur ${data.p1_count}.`}</p>}
      {plannedOut > 0 && <p className="dashboard-card-note">{`${plannedOut} sur ${rows.length} déjà prévue${plannedOut > 1 ? "s" : ""} hors fenêtre par le Dispatch (heure en rouge).`}</p>}
      <p className="dashboard-card-note">{p2.count ? `P2 : ${p2.ok} sur ${p2.count} dans la fenêtre · ${p2.early} en avance · ${p2.late} en retard · ${plural(p2.missing, "non faite")}.` : "Aucune P2 sur la période."}</p>
    </FoldCard>
  );
}

// Grand écran (1080p) : la vue tient dans la fenêtre, sans défilement de la page ; une liste longue défile dans sa carte.
// Hauteur = fenêtre de défilement − position de la vue ; en dessous de 1500 px de large ou de 620 px de haut, mise en page normale.
function useFitHeight(ref) {
  const [height, setHeight] = React.useState(null);
  React.useLayoutEffect(() => {
    const el = ref.current;
    let scroller = el.parentElement;
    while (scroller && scroller !== document.body && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
    if (!scroller || scroller === document.body) scroller = document.scrollingElement;
    const measure = () => {
      const page = scroller === document.scrollingElement;
      const top = el.getBoundingClientRect().top - (page ? 0 : scroller.getBoundingClientRect().top) + scroller.scrollTop;
      // Marges du bas des conteneurs jusqu'à la zone de défilement, pour ne pas la faire déborder de quelques pixels.
      let below = 4;
      for (let p = el.parentElement; p && p !== scroller.parentElement; p = p.parentElement) {
        const style = getComputedStyle(p);
        below += parseFloat(style.paddingBottom) + parseFloat(style.borderBottomWidth);
      }
      const available = Math.floor((page ? window.innerHeight : scroller.clientHeight) - top - below);
      // Plafond de 1000 px : sur un écran 4K, les graphiques ne s'étirent pas sur toute la hauteur.
      setHeight(el.parentElement.clientWidth >= 1500 && available >= 620 ? Math.min(available, 1000) : null);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el.parentElement);
    window.addEventListener("resize", measure);
    return () => { observer.disconnect(); window.removeEventListener("resize", measure); };
  }, [ref]);
  return height;
}

export default function GeotabDashboardOverview({ data, scopes, scope, metric, onOpenDay, onOpenView, onDrivers }) {
  const bodyRef = React.useRef(null);
  const fitHeight = useFitHeight(bodyRef);
  const context = <ContextCard data={data} scopes={scopes} scope={scope} metric={metric} onOpenDay={onOpenDay} />;
  return (
    <div ref={bodyRef} className="geotab-dash-body dashboard-page" data-fit={fitHeight ? "true" : undefined} style={fitHeight ? { height: fitHeight } : undefined}>
      <div className="geotab-dash-main">
        <div className="geotab-dash-row">
          <StagesCard scopes={scopes} scope={scope} metric={metric} onOpenDay={onOpenDay} />
          <WatchCard data={data} onOpenDay={onOpenDay} onOpenView={onOpenView} onDrivers={onDrivers} />
        </div>
        <div className="geotab-dash-trio">
          <Card title={`${metric.label} par semaine`} subtitle="Dimanche au samedi · pointillé : moyenne de la période">
            <WeekChart weeks={weekScopes(data.bar)} metric={metric} highlight={scope === "week" ? scopes.week?.start : null} average={valueOf(metric, scopes.period)} />
          </Card>
          <DriversCard data={data} scopes={scopes} scope={scope} metric={metric} onDrivers={onDrivers} onOpenDay={onOpenDay} />
          {context}
        </div>
      </div>
      <aside className="geotab-dash-side" aria-label="Autres mesures">
        <DrivingCard data={data} />
        <WeekdayCard scopes={scopes} metric={metric} />
        <PreloadFold data={data} onOpenDay={onOpenDay} />
        <P1Fold data={data} onOpenDay={onOpenDay} />
      </aside>
    </div>
  );
}
